import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  fetchLiveLeadNotification,
  formatFeedLine,
  hasSameFeedSecond,
  nextPollDelayMs,
  randomFirstFeedOffsetSeconds,
  subtractSecondsFromOccurredAt,
} from "@/lib/liveLeadNotification";

const MAX_VISIBLE_LINES = 3;

function splitFeedLine(line) {
  const match = /^\[([^\]]+)\]\s*(.*)$/.exec(line);
  if (!match) {
    return { time: null, rest: line };
  }
  return { time: match[1], rest: match[2] };
}

function LineCopy({ rest }) {
  const marker = " · Status: ";
  const idx = rest.lastIndexOf(marker);
  if (idx === -1) {
    return rest;
  }
  return (
    <>
      {rest.slice(0, idx)}
      <span className="system-activity-feed-status">{rest.slice(idx)}</span>
    </>
  );
}

const SystemActivityFeed = () => {
  const [lines, setLines] = useState([]);
  const reduceMotion = useReducedMotion();
  const cancelledRef = useRef(false);
  const timeoutRef = useRef(null);
  const nextIdRef = useRef(0);
  const firstLineCommittedRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const scheduleNext = () => {
      timeoutRef.current = window.setTimeout(tick, nextPollDelayMs());
    };

    const tick = async () => {
      const result = await fetchLiveLeadNotification();
      if (cancelledRef.current) {
        return;
      }
      if (result.ok && result.data) {
        const payload = firstLineCommittedRef.current
          ? result.data
          : {
              ...result.data,
              occurred_at: subtractSecondsFromOccurredAt(
                result.data.occurred_at,
                randomFirstFeedOffsetSeconds(),
              ),
            };
        const line = formatFeedLine(payload);
        if (line) {
          setLines((prev) => {
            if (hasSameFeedSecond(prev, line)) {
              return prev;
            }
            firstLineCommittedRef.current = true;
            const entry = { id: nextIdRef.current + 1, line };
            nextIdRef.current = entry.id;
            return [...prev, entry].slice(-MAX_VISIBLE_LINES);
          });
        }
      }
      if (!cancelledRef.current) {
        scheduleNext();
      }
    };

    tick();

    return () => {
      cancelledRef.current = true;
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="system-activity-feed" data-testid="system-activity-feed">
      <div className="system-activity-feed-chrome">
        <div className="system-activity-feed-chrome-left">
          <div className="system-activity-feed-dots" aria-hidden="true">
            <span className="system-activity-feed-dot system-activity-feed-dot-red" />
            <span className="system-activity-feed-dot system-activity-feed-dot-yellow" />
            <span className="system-activity-feed-dot system-activity-feed-dot-green" />
          </div>
          <span className="system-activity-feed-title">Protocol log</span>
        </div>
        <span className="system-activity-feed-live">
          <span className="system-activity-feed-live-dot" aria-hidden="true">
            <span className="system-activity-feed-live-ping" />
            <span className="system-activity-feed-live-core" />
          </span>
          <span className="system-activity-feed-live-label">Live</span>
        </span>
      </div>

      <div
        className="system-activity-feed-body"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {lines.length === 0 ? (
          <p className="system-activity-feed-waiting">
            <span className="system-activity-feed-prompt" aria-hidden="true">
              ›
            </span>
            listening for intercepts
            <span className="system-activity-feed-caret" aria-hidden="true" />
          </p>
        ) : (
          <AnimatePresence initial={false} mode="popLayout">
            {lines.map((entry, index) => {
              const { time, rest } = splitFeedLine(entry.line);
              const isLatest = index === lines.length - 1;
              return (
                <motion.p
                  key={entry.id}
                  layout={!reduceMotion}
                  initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: isLatest ? 1 : 0.48, y: 0 }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
                  transition={{ duration: reduceMotion ? 0 : 0.35, ease: [0.4, 0, 0.2, 1] }}
                  className="system-activity-feed-line"
                >
                  <span className="system-activity-feed-prompt" aria-hidden="true">
                    ›
                  </span>
                  {time ? <span className="system-activity-feed-time">[{time}]</span> : null}
                  <span className="system-activity-feed-copy">
                    <LineCopy rest={rest} />
                    {isLatest ? (
                      <span className="system-activity-feed-caret" aria-hidden="true" />
                    ) : null}
                  </span>
                </motion.p>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default SystemActivityFeed;
